import * as path from "path";
import * as fs from "fs";
import { glob } from "glob";

export interface DirectoryItem {
  name: string;
  type: "file" | "directory";
  path: string;
  size?: number;
  modified?: string;
  isHidden: boolean;
}

export interface ListDirectoryOptions {
  showHidden?: boolean;
  showDetails?: boolean;
  maxDepth?: number;
  ignorePatterns?: string[];
}

export async function listDirectory(
  dirPath: string,
  options: ListDirectoryOptions = {},
): Promise<DirectoryItem[]> {
  const {
    showHidden = false,
    showDetails = false,
    maxDepth = 1,
    ignorePatterns = ["node_modules", "dist", ".git"],
  } = options;

  const results: DirectoryItem[] = [];
  const resolvedPath = path.resolve(dirPath);

  try {
    // 检查路径是否存在
    if (!fs.existsSync(resolvedPath)) {
      throw new Error(`目录不存在: ${dirPath}`);
    }

    const stats = fs.statSync(resolvedPath);
    if (!stats.isDirectory()) {
      throw new Error(`路径不是目录: ${dirPath}`);
    }

    // 使用 fs.readdirSync 列出目录内容
    const items = fs.readdirSync(resolvedPath, { withFileTypes: true });

    for (const item of items) {
      const fileName = item.name;
      const isHidden = fileName.startsWith(".");
      const itemPath = path.join(resolvedPath, fileName);

      // 跳过隐藏文件（除非指定显示）
      if (isHidden && !showHidden) {
        continue;
      }

      // 跳过忽略的目录
      if (item.isDirectory() && ignorePatterns.includes(fileName)) {
        continue;
      }

      const itemStats = fs.statSync(itemPath);
      const relativePath = path.relative(resolvedPath, itemPath);

      const result: DirectoryItem = {
        name: fileName,
        type: item.isDirectory() ? "directory" : "file",
        path: relativePath,
        isHidden,
      };

      if (showDetails) {
        result.size = itemStats.size;
        result.modified = itemStats.mtime.toISOString().split("T")[0]; // YYYY-MM-DD 格式
      }

      results.push(result);
    }

    // 按类型和名称排序
    return results.sort((a, b) => {
      // 目录在前，文件在后
      if (a.type !== b.type) {
        return a.type === "directory" ? -1 : 1;
      }
      // 同类型按名称排序
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    throw new Error(`列出目录失败: ${error}`);
  }
}

// 递归列出目录（用于深度 > 1 的情况）
export async function listDirectoryRecursive(
  dirPath: string,
  options: ListDirectoryOptions = {},
): Promise<DirectoryItem[]> {
  const {
    showHidden = false,
    showDetails = false,
    maxDepth = 1,
    ignorePatterns = ["node_modules", "dist", ".git"],
  } = options;

  if (maxDepth <= 1) {
    return listDirectory(dirPath, options);
  }

  const results: DirectoryItem[] = [];
  const resolvedPath = path.resolve(dirPath);

  try {
    // 使用 glob 递归搜索
    const globPattern = "**/*";
    const ignoreGlob = ignorePatterns.map((pattern) => `**/${pattern}/**`);

    const files = await glob(globPattern, {
      cwd: resolvedPath,
      ignore: ignoreGlob,
      absolute: true,
      dot: showHidden,
      maxDepth: maxDepth - 1,
    });

    for (const filePath of files) {
      const relativePath = path.relative(resolvedPath, filePath);
      const fileName = path.basename(filePath);
      const isHidden = fileName.startsWith(".");

      // 跳过隐藏文件（除非指定显示）
      if (isHidden && !showHidden) {
        continue;
      }

      const stats = fs.statSync(filePath);
      const item: DirectoryItem = {
        name: fileName,
        type: stats.isDirectory() ? "directory" : "file",
        path: relativePath,
        isHidden,
      };

      if (showDetails) {
        item.size = stats.size;
        item.modified = stats.mtime.toISOString().split("T")[0];
      }

      results.push(item);
    }

    return results.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === "directory" ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    throw new Error(`递归列出目录失败: ${error}`);
  }
}
